import c from 'classnames';
import { useState } from 'react';

import type { ChatMessageMetadata } from '@repo/types';
import { Button, Typography } from '@repo/ui';
import { Markdown } from 'components/Markdown/Markdown';
import { AIMessageFeedback } from './AIMessageFeedback';
import { DataSourcesSection } from './DataSourcesSection';

import styles from './AIMessage.module.css';

interface AIMessageProps {
  message: string;
  messageId: string;
  isStreaming: boolean;
  hasFeedback: boolean;
  metadataJson?: ChatMessageMetadata | null;
  onRetry?: () => void;
  className?: string;
}

export const AIMessage = ({
  message,
  messageId,
  isStreaming,
  hasFeedback,
  metadataJson,
  onRetry,
  className,
}: AIMessageProps) => {
  // Decided once at mount: subsequent hasFeedback flips (e.g. optimistic
  // cache update on submit) must not yank the widget mid-fade. The widget
  // hides itself via onDismiss after its exit animation completes.
  const [showFeedbackWidget, setShowFeedbackWidget] = useState(
    () => !hasFeedback,
  );

  const meta = metadataJson ?? null;
  const isGeneralError =
    meta?.isError === true && meta?.errorCategory !== 'content_boundary';

  if (isGeneralError) {
    return (
      <div className={c(styles.aiMessage, styles.errorMessage, className)}>
        <Typography variant='body-s' className={styles.errorText}>
          Oh no! Something went wrong!
        </Typography>
        {onRetry && (
          <Button
            variant='text'
            size='small'
            color='purple'
            iconNameLeft='refreshCcw'
            label='Try again'
            onClick={onRetry}
            className={c(styles.retryButton, 'mt-2')}
          />
        )}
      </div>
    );
  }

  const sources = meta?.sources ?? [];

  return (
    <div className={c(styles.aiMessage, className)}>
      <Markdown className={styles.messageContent}>{message}</Markdown>
      {!isStreaming && sources.length > 0 && (
        <DataSourcesSection sources={sources} />
      )}
      {!isStreaming && showFeedbackWidget && (
        <AIMessageFeedback
          messageId={messageId}
          isStreaming={isStreaming}
          onDismiss={() => setShowFeedbackWidget(false)}
        />
      )}
    </div>
  );
};
